import { useState } from 'react'

interface AccessError {
  error: string
  code: string
  subscriptionStatus?: string
  subscriptionTier?: string
  currentTier?: string
  expiresAt?: string
  upgradeRequired?: boolean
  premiumRequired?: boolean
  supportContact?: boolean
}

interface AccessErrorState {
  isOpen: boolean
  errorCode: string
  errorMessage: string
  subscriptionStatus?: string
  subscriptionTier?: string
  expiresAt?: string
  upgradeRequired?: boolean
  premiumRequired?: boolean
  supportContact?: boolean
}

export function useAccessErrorHandler() {
  const [errorState, setErrorState] = useState<AccessErrorState>({
    isOpen: false,
    errorCode: '',
    errorMessage: ''
  })

  const handleAccessError = (error: any) => {
    // Check if this is an access control error from our middleware
    if (error?.status === 402 || error?.status === 403) {
      try {
        const errorData: AccessError = error.data || {}
        
        setErrorState({
          isOpen: true,
          errorCode: errorData.code || 'ACCESS_DENIED',
          errorMessage: errorData.error || 'Zugang verweigert',
          subscriptionStatus: errorData.subscriptionStatus,
          subscriptionTier: errorData.subscriptionTier || errorData.currentTier,
          expiresAt: errorData.expiresAt,
          upgradeRequired: errorData.upgradeRequired,
          premiumRequired: errorData.premiumRequired,
          supportContact: errorData.supportContact
        })
        
        return true // Handled
      } catch (e) {
        console.error('Failed to parse access error:', e)
      }
    }
    
    return false // Not handled
  }

  const clearError = () => {
    setErrorState({
      isOpen: false,
      errorCode: '',
      errorMessage: ''
    })
  }

  // Hook into TanStack Query's error handling
  const queryErrorHandler = (error: any) => {
    if (handleAccessError(error)) {
      // Error was handled, prevent other error handling
      return
    }
    // Let other error handlers deal with it
    throw error
  }

  return {
    errorState,
    handleAccessError,
    clearError,
    queryErrorHandler
  }
}